import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Archive, ArchiveRestore, ArrowLeft, Loader2, Pencil, Plus } from 'lucide-react';
import { toast } from 'sonner';
import { Badge } from '../../../components/ui/badge';
import { Button } from '../../../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../../components/ui/card';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Textarea } from '../../../components/ui/textarea';
import { MarkdownEditor } from '../../../components/ui/markdown-editor';
import { MarkdownText } from '../../../components/ui/markdown-text';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../../components/ui/select';
import { usePermission } from '../../../components/rbac/use-permission';
import { P } from '../../../lib/rbac/permissions.generated';
import {
  abnahmeApi,
  STORY_STATUS_LABEL,
  type KatalogDto,
  type KriteriumDto,
  type PruefAnleitungDto,
  type StoryDetailDto,
  type StoryPatchInput,
  type StoryStatus,
} from '../../../lib/api/abnahme';
import StoryStatusBadge from '../../../components/abnahme/StoryStatusBadge';
import PruefAnleitung from '../../../components/abnahme/PruefAnleitung';
import { TestAbdeckung, TestListe } from '../../../components/abnahme/TestAbdeckung';
import StoryTestfaelle from '../../../components/abnahme/StoryTestfaelle';
import StoryTicketListe from '../../../components/abnahme/StoryTicketListe';

const zeit = (iso: string | null): string => {
  if (!iso) return '—';
  const datum = new Date(iso);
  return Number.isNaN(datum.getTime()) ? '—' : datum.toLocaleString('de-DE');
};

/**
 * Eine Story: Beschreibung, Akzeptanzkriterien, Stand, verknüpfte Tickets, Verlauf.
 *
 * Ändert sich Text oder ein Kriterium einer abgenommenen Story, fällt sie auf
 * „Geplant" zurück — das entscheidet der Server und steht danach im Verlauf.
 */
export default function StoryDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const darfBearbeiten = usePermission(P.abnahme.katalog.katalog_bearbeiten);
  const darfTesten = usePermission(P.abnahme.testlaeufe.testlaeufe_durchfuehren);

  const [story, setStory] = useState<StoryDetailDto | null>(null);
  const [epics, setEpics] = useState<KatalogDto['epics']>([]);
  const [fehler, setFehler] = useState<string | null>(null);
  const [speichert, setSpeichert] = useState(false);
  const [entwurf, setEntwurf] = useState<{ title: string; description: string } | null>(null);
  const [neuesKriterium, setNeuesKriterium] = useState('');
  const [kriteriumEntwurf, setKriteriumEntwurf] = useState<{ id: number; text: string } | null>(null);
  const [zielStatus, setZielStatus] = useState<StoryStatus | ''>('');
  const [version, setVersion] = useState('');
  const [archivZeigen, setArchivZeigen] = useState(false);

  const laden = useCallback(async () => {
    const nummer = Number(id);
    if (!Number.isFinite(nummer)) {
      setFehler('Diese Story gibt es nicht.');
      return;
    }
    try {
      setStory(await abnahmeApi.story(nummer));
      setFehler(null);
    } catch (grund) {
      setFehler(grund instanceof Error ? grund.message : 'Die Story ließ sich nicht laden.');
    }
  }, [id]);

  useEffect(() => {
    void laden();
  }, [laden]);

  useEffect(() => {
    if (!darfBearbeiten) return;
    abnahmeApi
      .katalog()
      .then((daten) => setEpics(daten?.epics ?? []))
      .catch(() => setEpics([]));
  }, [darfBearbeiten]);

  const speichern = async (arbeit: () => Promise<unknown>, meldung = 'Gespeichert.') => {
    setSpeichert(true);
    try {
      await arbeit();
      // Neu laden statt die Antwort zu übernehmen: Kriterien und Verlauf ändern
      // sich oft mit, und der Server entscheidet über die Rückstufung.
      await laden();
      toast.success(meldung);
      return true;
    } catch (grund) {
      toast.error(grund instanceof Error ? grund.message : 'Die Änderung wurde nicht übernommen.');
      return false;
    } finally {
      setSpeichert(false);
    }
  };

  const aendern = (input: StoryPatchInput, meldung?: string) =>
    story ? speichern(() => abnahmeApi.storyAendern(story.id, input), meldung) : Promise.resolve(false);

  if (fehler) {
    return (
      <div className="mx-auto max-w-3xl space-y-4">
        <Button variant="ghost" size="sm" className="gap-1.5" onClick={() => navigate('/abnahme')}>
          <ArrowLeft className="h-4 w-4" /> Zum Katalog
        </Button>
        <div className="rounded-lg border border-dashed p-6 text-sm text-muted-foreground">{fehler}</div>
      </div>
    );
  }

  if (!story) {
    return (
      <div className="flex items-center gap-2 text-sm text-muted-foreground">
        <Loader2 className="h-4 w-4 animate-spin" /> Story wird geladen …
      </div>
    );
  }

  const kriterien = story.kriterien.filter((kriterium) => archivZeigen || !kriterium.archived);
  const archivierteKriterien = story.kriterien.filter((kriterium) => kriterium.archived).length;

  return (
    <div className="mx-auto max-w-6xl space-y-6">
      <div className="space-y-3">
        <Button variant="ghost" size="sm" className="gap-1.5" onClick={() => navigate('/abnahme')}>
          <ArrowLeft className="h-4 w-4" /> Zum Katalog
        </Button>
        <div className="flex flex-wrap items-center gap-2">
          <Badge variant="outline" className="font-mono">
            {story.key}
          </Badge>
          <StoryStatusBadge status={story.status} />
          {story.readyVersion && <Badge variant="secondary">Version {story.readyVersion}</Badge>}
          {story.archived && <Badge variant="secondary">archiviert</Badge>}
          <span className="text-sm text-muted-foreground">
            {story.epic.key} · {story.epic.title}
          </span>
        </div>
        {entwurf ? null : (
          <div className="flex items-start gap-2">
            <h1 className="text-2xl font-semibold leading-8">{story.title}</h1>
            {darfBearbeiten && (
              <Button
                variant="ghost"
                size="sm"
                className="gap-1.5"
                onClick={() => setEntwurf({ title: story.title, description: story.description })}
              >
                <Pencil className="h-3.5 w-3.5" /> Bearbeiten
              </Button>
            )}
          </div>
        )}
      </div>

      <div className="grid gap-6 lg:grid-cols-[1fr_20rem]">
        <div className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Beschreibung</CardTitle>
              {story.status === 'abgenommen' && darfBearbeiten && (
                <CardDescription>
                  Abgenommen. Wer Text oder Kriterien ändert, setzt die Story auf „Geplant“ zurück —
                  die Freigabe galt dem bisherigen Stand.
                </CardDescription>
              )}
            </CardHeader>
            <CardContent>
              {entwurf ? (
                <div className="space-y-3">
                  <div className="space-y-1">
                    <Label htmlFor="story-titel">Titel</Label>
                    <Input
                      id="story-titel"
                      value={entwurf.title}
                      onChange={(event) => setEntwurf({ ...entwurf, title: event.target.value })}
                    />
                  </div>
                  <div className="space-y-1">
                    <Label htmlFor="story-text">Beschreibung</Label>
                    <MarkdownEditor
                      id="story-text"
                      rows={10}
                      value={entwurf.description}
                      onChange={(wert) => setEntwurf({ ...entwurf, description: wert })}
                    />
                  </div>
                  <div className="flex gap-2">
                    <Button
                      disabled={speichert || !entwurf.title.trim()}
                      onClick={async () => {
                        const ok = await aendern({
                          title: entwurf.title.trim(),
                          description: entwurf.description,
                        });
                        if (ok) setEntwurf(null);
                      }}
                    >
                      Speichern
                    </Button>
                    <Button variant="ghost" onClick={() => setEntwurf(null)}>
                      Abbrechen
                    </Button>
                  </div>
                </div>
              ) : story.description ? (
                <MarkdownText text={story.description} className="text-sm" />
              ) : (
                <p className="text-sm text-muted-foreground">Keine Beschreibung.</p>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-start justify-between gap-4 space-y-0">
              <div>
                <CardTitle className="text-base">Akzeptanzkriterien</CardTitle>
                <CardDescription>
                  Woran sich „fertig“ messen lässt. Ein Test gehört zu einem Kriterium, wenn er
                  dessen Schlüssel trägt — pytest: <code>@pytest.mark.story("{story.key}.1")</code>,
                  Vitest: <code>[{story.key}.1]</code> im Testnamen.
                </CardDescription>
                <div className="mt-2">
                  <TestAbdeckung tests={story.tests} />
                </div>
              </div>
              {archivierteKriterien > 0 && (
                <label className="flex shrink-0 items-center gap-2 text-xs text-muted-foreground">
                  <input
                    type="checkbox"
                    checked={archivZeigen}
                    onChange={(event) => setArchivZeigen(event.target.checked)}
                  />
                  {archivierteKriterien} archiviert
                </label>
              )}
            </CardHeader>
            <CardContent className="space-y-3">
              {kriterien.length === 0 && (
                <p className="text-sm text-muted-foreground">Noch keine Kriterien.</p>
              )}
              {kriterien.map((kriterium) => (
                <KriteriumZeile
                  key={kriterium.id}
                  kriterium={kriterium}
                  darfBearbeiten={darfBearbeiten}
                  speichert={speichert}
                  entwurf={kriteriumEntwurf?.id === kriterium.id ? kriteriumEntwurf.text : null}
                  onEntwurf={(text) => setKriteriumEntwurf(text === null ? null : { id: kriterium.id, text })}
                  onSpeichern={async (input) => {
                    const ok = await speichern(() => abnahmeApi.kriteriumAendern(kriterium.id, input));
                    if (ok) setKriteriumEntwurf(null);
                    return Boolean(ok);
                  }}
                />
              ))}
              {(story.storyTests?.length ?? 0) > 0 && (
                <div className="space-y-1 border-t pt-3">
                  <p className="text-xs font-medium text-muted-foreground">
                    Tests zur ganzen Story ({story.key})
                  </p>
                  <TestListe tests={story.storyTests} />
                </div>
              )}
              {darfBearbeiten && (
                <div className="flex flex-col gap-2 border-t pt-3 sm:flex-row">
                  <Textarea
                    rows={2}
                    value={neuesKriterium}
                    placeholder="z. B. Eine Kündigung nach Fristende wird mit Hinweis abgelehnt"
                    onChange={(event) => setNeuesKriterium(event.target.value)}
                  />
                  <Button
                    className="gap-1.5 sm:self-end"
                    disabled={speichert || !neuesKriterium.trim()}
                    onClick={async () => {
                      const ok = await speichern(
                        () => abnahmeApi.kriteriumAnlegen(story.id, neuesKriterium.trim()),
                        'Kriterium ergänzt.',
                      );
                      if (ok) setNeuesKriterium('');
                    }}
                  >
                    <Plus className="h-4 w-4" /> Ergänzen
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>

          <StoryTestfaelle
            storyId={story.id}
            testfaelle={story.testfaelle ?? []}
            kriterien={story.kriterien}
            darfBearbeiten={darfTesten}
            onGeaendert={laden}
          />

          <Card>
            <CardHeader>
              <CardTitle className="text-base">Verlauf</CardTitle>
            </CardHeader>
            <CardContent>
              {story.history.length === 0 ? (
                <p className="text-sm text-muted-foreground">Noch nichts passiert.</p>
              ) : (
                <ul className="space-y-2 text-sm">
                  {story.history.map((eintrag) => (
                    <li key={eintrag.id}>
                      <span className="text-muted-foreground">
                        {zeit(eintrag.createdAt)} · {eintrag.actorName ?? 'System'}:
                      </span>{' '}
                      {eintrag.description}
                    </li>
                  ))}
                </ul>
              )}
            </CardContent>
          </Card>
        </div>

        <div className="space-y-6">
          {darfBearbeiten && (
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Stand</CardTitle>
                <CardDescription>
                  Abnehmen und ablehnen geschieht unter „Freigabe“ durch einen Super-Admin, nicht hier.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-3">
                <Select
                  value={zielStatus}
                  onValueChange={(wert) => setZielStatus(wert as StoryStatus)}
                  disabled={speichert || story.allowedStatus.length === 0}
                >
                  <SelectTrigger>
                    <SelectValue placeholder={`${STORY_STATUS_LABEL[story.status]} → …`} />
                  </SelectTrigger>
                  <SelectContent>
                    {story.allowedStatus.map((wert) => (
                      <SelectItem key={wert} value={wert}>
                        {STORY_STATUS_LABEL[wert]}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {zielStatus === 'bereit_zur_abnahme' && (
                  <div className="space-y-1">
                    <Label htmlFor="story-version">Version auf dem Testsystem</Label>
                    <Input
                      id="story-version"
                      value={version}
                      placeholder="z. B. 0.0.560"
                      onChange={(event) => setVersion(event.target.value)}
                    />
                  </div>
                )}
                <Button
                  className="w-full"
                  disabled={
                    speichert || !zielStatus || (zielStatus === 'bereit_zur_abnahme' && !version.trim())
                  }
                  onClick={async () => {
                    if (!zielStatus) return;
                    const ok = await aendern(
                      zielStatus === 'bereit_zur_abnahme'
                        ? { status: zielStatus, readyVersion: version.trim() }
                        : { status: zielStatus },
                    );
                    if (ok) {
                      setZielStatus('');
                      setVersion('');
                    }
                  }}
                >
                  Übernehmen
                </Button>

                {epics.length > 1 && (
                  <div className="space-y-1 border-t pt-3">
                    <Label>Epic</Label>
                    <Select
                      value={String(story.epicId)}
                      onValueChange={(wert) =>
                        void aendern({ epicId: Number(wert) }, 'Verschoben — der Schlüssel bleibt.')
                      }
                      disabled={speichert}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {epics
                          .filter((epic) => !epic.archived || epic.id === story.epicId)
                          .map((epic) => (
                            <SelectItem key={epic.id} value={String(epic.id)}>
                              {epic.key} · {epic.title}
                            </SelectItem>
                          ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}

                <Button
                  variant="outline"
                  size="sm"
                  className="w-full gap-1.5"
                  disabled={speichert}
                  onClick={() =>
                    void aendern(
                      { archived: !story.archived },
                      story.archived ? 'Aus dem Archiv geholt.' : 'Archiviert.',
                    )
                  }
                >
                  {story.archived ? (
                    <>
                      <ArchiveRestore className="h-3.5 w-3.5" /> Aus dem Archiv holen
                    </>
                  ) : (
                    <>
                      <Archive className="h-3.5 w-3.5" /> Archivieren
                    </>
                  )}
                </Button>
              </CardContent>
            </Card>
          )}

          <Card>
            <CardHeader>
              <CardTitle className="text-base">Freigabe</CardTitle>
              <CardDescription>Jede Entscheidung mit Person, Zeitpunkt und Version.</CardDescription>
            </CardHeader>
            <CardContent>
              {(story.freigaben ?? []).length === 0 ? (
                <p className="text-sm text-muted-foreground">
                  {story.status === 'bereit_zur_abnahme'
                    ? 'Steht zur Abnahme bereit — entschieden wird unter „Freigabe“.'
                    : 'Noch nicht zur Abnahme vorgelegt.'}
                </p>
              ) : (
                <ul className="space-y-2 text-sm">
                  {(story.freigaben ?? []).map((eintrag) => (
                    <li key={eintrag.id}>
                      <span
                        className={
                          eintrag.entscheidung === 'abgenommen'
                            ? 'font-medium text-emerald-700 dark:text-emerald-400'
                            : 'font-medium text-red-600 dark:text-red-400'
                        }
                      >
                        {eintrag.entscheidung === 'abgenommen' ? 'Abgenommen' : 'Abgelehnt'}
                      </span>
                      <span className="block text-xs text-muted-foreground">
                        {eintrag.name ?? 'unbekannt'} · {zeit(eintrag.createdAt)} · Version{' '}
                        {eintrag.version ?? '—'}
                        {eintrag.ticketRef ? ` · Ticket ${eintrag.ticketRef}` : ''}
                      </span>
                      {eintrag.begruendung && (
                        <span className="mt-0.5 block whitespace-pre-wrap text-xs">{eintrag.begruendung}</span>
                      )}
                    </li>
                  ))}
                </ul>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-base">Tickets</CardTitle>
              <CardDescription>
                Offene und erledigte Tickets dieses Systems mit Story {story.key}, dazu die offenen
                vom Testsystem (Stand des letzten Abgleichs).
              </CardDescription>
            </CardHeader>
            <CardContent>
              <StoryTicketListe tickets={story.ticketList} fremdTickets={story.fremdTickets} />
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}

function KriteriumZeile({
  kriterium,
  darfBearbeiten,
  speichert,
  entwurf,
  onEntwurf,
  onSpeichern,
}: {
  kriterium: KriteriumDto;
  darfBearbeiten: boolean;
  speichert: boolean;
  entwurf: string | null;
  onEntwurf: (text: string | null) => void;
  onSpeichern: (input: {
    text?: string;
    archived?: boolean;
    anleitung?: Omit<PruefAnleitungDto, 'quelle'> | null;
  }) => void | Promise<boolean>;
}) {
  if (entwurf !== null) {
    return (
      <div className="space-y-2">
        <Textarea rows={2} value={entwurf} onChange={(event) => onEntwurf(event.target.value)} />
        <div className="flex gap-2">
          <Button size="sm" disabled={speichert || !entwurf.trim()} onClick={() => onSpeichern({ text: entwurf.trim() })}>
            Speichern
          </Button>
          <Button size="sm" variant="ghost" onClick={() => onEntwurf(null)}>
            Abbrechen
          </Button>
        </div>
      </div>
    );
  }
  return (
    <div className={`flex items-start gap-3 ${kriterium.archived ? 'opacity-60' : ''}`}>
      <span className="mt-0.5 shrink-0 font-mono text-xs text-muted-foreground">{kriterium.key}</span>
      <div className="flex-1 space-y-1">
        <p className="whitespace-pre-wrap text-sm">{kriterium.text}</p>
        {!kriterium.archived && <TestListe tests={kriterium.tests} />}
        {!kriterium.archived && (
          <PruefAnleitung
            anleitung={kriterium.anleitung}
            onSpeichern={
              darfBearbeiten
                ? async (anleitung) => (await onSpeichern({ anleitung })) !== false
                : undefined
            }
          />
        )}
      </div>
      {darfBearbeiten && (
        <div className="flex shrink-0 gap-1">
          {!kriterium.archived && (
            <Button
              variant="ghost"
              size="sm"
              aria-label={`${kriterium.key} bearbeiten`}
              onClick={() => onEntwurf(kriterium.text)}
            >
              <Pencil className="h-3.5 w-3.5" />
            </Button>
          )}
          <Button
            variant="ghost"
            size="sm"
            disabled={speichert}
            aria-label={kriterium.archived ? `${kriterium.key} zurückholen` : `${kriterium.key} archivieren`}
            onClick={() => onSpeichern({ archived: !kriterium.archived })}
          >
            {kriterium.archived ? <ArchiveRestore className="h-3.5 w-3.5" /> : <Archive className="h-3.5 w-3.5" />}
          </Button>
        </div>
      )}
    </div>
  );
}
